import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Clock, ArrowRight, BookOpen, Compass, User, ChevronLeft, ChevronRight } from 'lucide-react';
import { sanityClient, urlFor, SanityPost, SanityCategory } from '../lib/sanity';
import { updateSEO } from '../lib/seo';
import { Language } from '../translations';
import { getLocalizedPath } from '../lib/i18n';

interface BlogProps {
    t: any;
    lang: Language;
}

const FALLBACK_BLOG_IMAGE = 'https://iuixlsqrlfkfnvnckgij.supabase.co/storage/v1/object/public/expeditions/Laguna%20Cerro%20Castillo/trekking-laguna-cerro-castillo-turquesa.jpg';
const GRID_PER_PAGE = 9;

const Blog: React.FC<BlogProps> = ({ t, lang }) => {
    const [posts, setPosts] = useState<SanityPost[]>([]);
    const [totalCount, setTotalCount] = useState<number>(0);
    const [categories, setCategories] = useState<SanityCategory[]>([]);
    const [selectedCategory, setSelectedCategory] = useState<string>('all');
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [loading, setLoading] = useState(true);

    // 1. Cargar categorías de Sanity
    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const categoriesQuery = `*[_type == "category"] | order(title asc) {
                    _id,
                    title,
                    slug
                }`;
                const categoriesData = await sanityClient.fetch<SanityCategory[]>(categoriesQuery);
                setCategories(categoriesData || []);
            } catch (error) {
                console.error('Error cargando categorías desde Sanity:', error);
            }
        };

        fetchCategories();
    }, []);

    // 2. Cargar posts paginados desde Sanity según idioma, categoría y página actual
    useEffect(() => {
        let isCancelled = false;

        const fetchBlogData = async () => {
            setLoading(true);
            try {
                // Cálculo de rango para Sanity GROQ:
                // Página 1: 0..10 (1 destacado + 9 grilla)
                // Página > 1: 9 por página
                const isFirstPage = currentPage === 1;
                const start = isFirstPage ? 0 : 10 + (currentPage - 2) * GRID_PER_PAGE;
                const end = isFirstPage ? 10 : start + GRID_PER_PAGE;

                const postsQuery = `{
                    "total": count(*[_type == "post" && !(_id in path("drafts.**")) && (language == $lang || (!defined(language) && $lang == 'es')) && ($category == "all" || $category in categories[]->slug.current || $category in categories[]->_id)]),
                    "posts": *[_type == "post" && !(_id in path("drafts.**")) && (language == $lang || (!defined(language) && $lang == 'es')) && ($category == "all" || $category in categories[]->slug.current || $category in categories[]->_id)] | order(publishedAt desc) [$start...$end] {
                        _id,
                        title,
                        slug,
                        language,
                        publishedAt,
                        excerpt,
                        mainImage,
                        categories[]->{ _id, title, slug },
                        author->{ _id, name, role, image },
                        readTimeMinutes
                    }
                }`;

                let result = await sanityClient.fetch<{ total: number; posts: SanityPost[] }>(postsQuery, {
                    lang,
                    category: selectedCategory,
                    start,
                    end
                });

                // Fallback de resguardo: Si en el idioma seleccionado aún no hay publicaciones disponibles,
                // cargamos los artículos en español para garantizar que el blog nunca quede vacío.
                if ((!result?.posts || result.posts.length === 0) && lang !== 'es') {
                    const fallbackResult = await sanityClient.fetch<{ total: number; posts: SanityPost[] }>(postsQuery, {
                        lang: 'es',
                        category: selectedCategory,
                        start,
                        end
                    });
                    if (fallbackResult?.posts && fallbackResult.posts.length > 0) {
                        result = fallbackResult;
                    }
                }

                if (!isCancelled) {
                    setPosts(result?.posts || []);
                    setTotalCount(result?.total || 0);
                }
            } catch (error) {
                console.error('Error cargando blog desde Sanity:', error);
                if (!isCancelled) {
                    setPosts([]);
                    setTotalCount(0);
                }
            } finally {
                if (!isCancelled) {
                    setLoading(false);
                }
            }
        };

        fetchBlogData();

        return () => {
            isCancelled = true;
        };
    }, [lang, selectedCategory, currentPage]);

    // 3. Actualizar SEO
    useEffect(() => {
        updateSEO({
            title: currentPage > 1
                ? `${t.blog_title || 'Blog: Consejos y Relatos'} - ${t.blog_page || 'Página'} ${currentPage} | Fisura Expeditions`
                : `${t.blog_title || 'Blog: Consejos y Relatos de Montaña'} | Fisura Expeditions`,
            description: t.blog_subtitle || 'Historias de montaña, guías técnicas y recomendaciones para explorar Aysén.',
            keywords: ['Blog Fisura Expeditions', 'Guías de Montaña', 'Cerro Castillo', 'Trekking Aysén', 'Escalada Aysén'],
            url: typeof window !== 'undefined' ? window.location.href : 'https://fisuraexpeditions.com/blog',
            type: 'website'
        });
    }, [currentPage, lang, t]);

    // Cálculo de páginas totales
    // Página 1 contiene hasta 10 artículos (1 grande + 9 grilla).
    // Las páginas subsiguientes contienen hasta 9 artículos cada una.
    const totalPages = totalCount <= 10
        ? (totalCount > 0 ? 1 : 0)
        : 1 + Math.ceil((totalCount - 10) / GRID_PER_PAGE);

    // Si la página actual excede el total (por cambio de categoría), reajustar a 1
    useEffect(() => {
        if (totalPages > 0 && currentPage > totalPages) {
            setCurrentPage(1);
        }
    }, [totalPages, currentPage]);

    const handleCategoryChange = (catId: string) => {
        if (catId === selectedCategory) return;
        setSelectedCategory(catId);
        setCurrentPage(1);
        const topEl = document.getElementById('blog-content-top');
        if (topEl) {
            topEl.scrollIntoView({ behavior: 'smooth' });
        }
    };

    const handlePageChange = (page: number) => {
        if (page === currentPage || page < 1 || page > totalPages) return;
        setCurrentPage(page);
        const topEl = document.getElementById('blog-content-top');
        if (topEl) {
            topEl.scrollIntoView({ behavior: 'smooth' });
        } else {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    const formatDate = (dateString: string) => {
        try {
            const locale = lang === 'en' ? 'en-US' : lang === 'pt' ? 'pt-BR' : 'es-CL';
            return new Date(dateString).toLocaleDateString(locale, {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            });
        } catch {
            return dateString;
        }
    };

    const getPostImageUrl = (post: SanityPost, isFeatured: boolean = false): string => {
        if (post.mainImage?.asset) {
            if (isFeatured) {
                return urlFor(post.mainImage).width(1200).height(800).quality(85).auto('format').url();
            }
            return urlFor(post.mainImage).width(800).height(533).quality(80).auto('format').url();
        }
        return FALLBACK_BLOG_IMAGE;
    };

    // Generador de números de página con elipsis (...) para paginación elegante
    const getPaginationNumbers = (current: number, total: number): (number | string)[] => {
        if (total <= 7) {
            return Array.from({ length: total }, (_, i) => i + 1);
        }
        if (current <= 4) {
            return [1, 2, 3, 4, 5, '...', total];
        }
        if (current >= total - 3) {
            return [1, '...', total - 4, total - 3, total - 2, total - 1, total];
        }
        return [1, '...', current - 1, current, current + 1, '...', total];
    };

    // En la página 1, el primer post es el grande destacado, y el resto (máximo 9) van a la grilla.
    // En páginas 2+, todos los posts (máximo 9) se presentan en la grilla.
    const featuredPost = currentPage === 1 ? (posts[0] || null) : null;
    const gridPosts = currentPage === 1 ? posts.slice(1) : posts;

    return (
        <div className="bg-[#0a0a0a] min-h-screen text-[#f2f2f2] pt-6 md:pt-8 pb-24">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                
                {/* 1. Header / Hero del Blog */}
                <div className="border-b border-rock/80 pb-10 mb-12">
                    <div className="flex items-center gap-2 text-[#00d4ff] text-[10px] font-bold uppercase tracking-[0.3em] mb-4">
                        <Compass className="w-3.5 h-3.5" />
                        <Link to={getLocalizedPath('/', lang)} className="hover:underline">{t.nav_home}</Link>
                        <span>/</span>
                        <span className="text-gray-400">BLOG</span>
                        {currentPage > 1 && (
                            <>
                                <span>/</span>
                                <span className="text-[#00d4ff]">{t.blog_page || 'PÁGINA'} {currentPage}</span>
                            </>
                        )}
                    </div>

                    <h1 className="text-4xl sm:text-5xl md:text-6xl font-title uppercase tracking-tighter text-white leading-none mb-4">
                        {t.blog_title || 'Blog: Consejos y Relatos de Montaña'}
                    </h1>
                    <p className="text-gray-400 text-sm sm:text-base max-w-2xl font-light leading-relaxed">
                        {t.blog_subtitle || 'Historias, guías técnicas y recomendaciones de montaña en Aysén.'}
                    </p>

                    {/* Filtro de Categorías */}
                    {categories.length > 0 && (
                        <div className="flex items-center gap-2 pt-8 overflow-x-auto pb-2 custom-admin-scroll">
                            <button
                                onClick={() => handleCategoryChange('all')}
                                className={`px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider transition-all whitespace-nowrap border ${
                                    selectedCategory === 'all'
                                    ? 'bg-[#00d4ff] text-black border-[#00d4ff] shadow-lg shadow-[#00d4ff]/20'
                                    : 'bg-white/5 border-white/10 text-gray-300 hover:border-white/30'
                                }`}
                            >
                                {t.cat_all || 'Todos'}
                            </button>
                            {categories.map(cat => {
                                const catKey = cat.slug?.current || cat._id;
                                const isSelected = selectedCategory === catKey;
                                return (
                                    <button
                                        key={cat._id}
                                        onClick={() => handleCategoryChange(catKey)}
                                        className={`px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider transition-all whitespace-nowrap border ${
                                            isSelected
                                            ? 'bg-[#00d4ff] text-black border-[#00d4ff] shadow-lg shadow-[#00d4ff]/20'
                                            : 'bg-white/5 border-white/10 text-gray-300 hover:border-white/30'
                                        }`}
                                    >
                                        {cat.title}
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Ancla para scroll suave al cambiar página o categoría */}
                <div id="blog-content-top" className="scroll-mt-28" />

                {/* Indicador de página secundaria */}
                {currentPage > 1 && !loading && (
                    <div className="flex items-center justify-between pb-6 border-b border-rock/60 mb-10">
                        <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-400">
                            <span className="text-[#00d4ff] font-bold uppercase tracking-wider text-xs">
                                {t.blog_page || 'Página'} {currentPage} {t.blog_of || 'de'} {totalPages}
                            </span>
                            <span>•</span>
                            <span className="text-xs text-gray-500">{totalCount} {t.blog_articles_total || 'artículos en total'}</span>
                        </div>
                        <button
                            onClick={() => handlePageChange(1)}
                            className="text-xs text-[#00d4ff] hover:underline flex items-center gap-1 cursor-pointer transition-colors"
                        >
                            ← {t.blog_back_first_page || 'Volver al artículo destacado'}
                        </button>
                    </div>
                )}

                {/* 2. Estado de Carga (Skeleton progresivo) */}
                {loading && (
                    <div className="space-y-12">
                        {currentPage === 1 && (
                            <div className="h-[420px] bg-white/5 rounded-2xl animate-pulse border border-white/10" />
                        )}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                            {[1, 2, 3, 4, 5, 6].map(n => (
                                <div key={n} className="h-80 bg-white/5 rounded-xl animate-pulse border border-white/10" />
                            ))}
                        </div>
                    </div>
                )}

                {/* 3. Estado Vacío (Sin artículos en la categoría o idioma) */}
                {!loading && posts.length === 0 && (
                    <div className="text-center py-20 px-4 border border-rock/60 rounded-2xl bg-gradient-to-b from-[#1a1512]/60 to-transparent">
                        <div className="w-16 h-16 rounded-full bg-[#00d4ff]/10 border border-[#00d4ff]/30 text-[#00d4ff] flex items-center justify-center mx-auto mb-6">
                            <BookOpen className="w-8 h-8" />
                        </div>
                        <h3 className="text-2xl font-title uppercase tracking-wider text-white mb-2">
                            {t.blog_title || 'Próximamente nuevos relatos'}
                        </h3>
                        <p className="text-gray-400 text-sm max-w-md mx-auto mb-8 font-light">
                            {t.blog_empty || 'Pronto publicaremos nuevos artículos y guías de montaña. ¡Vuelve pronto!'}
                        </p>
                        <Link
                            to={getLocalizedPath('/expeditions', lang)}
                            className="inline-flex items-center gap-2 bg-[#00d4ff] hover:bg-white text-black px-6 py-3 font-title text-sm uppercase tracking-widest transition-all duration-300"
                        >
                            <span>{t.nav_expeditions || 'Ver Expediciones'}</span>
                            <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>
                )}

                {/* 4. Listado de Artículos */}
                {!loading && posts.length > 0 && (
                    <div className="space-y-16">
                        
                        {/* Artículo Destacado (solo en la primera página) */}
                        {featuredPost && (
                            <div className="relative group overflow-hidden rounded-2xl border border-rock bg-[#14100d] transition-all duration-500 hover:border-[#00d4ff]/50 shadow-2xl">
                                <Link to={getLocalizedPath(`/blog/${featuredPost.slug.current}`, lang)} className="grid grid-cols-1 lg:grid-cols-12 gap-0">
                                    <div className="lg:col-span-7 h-72 sm:h-96 lg:h-[450px] overflow-hidden relative">
                                        <img 
                                            src={getPostImageUrl(featuredPost, true)} 
                                            alt={featuredPost.title}
                                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent lg:hidden" />
                                    </div>
                                    <div className="lg:col-span-5 p-8 sm:p-10 flex flex-col justify-between">
                                        <div>
                                            <div className="flex items-center gap-3 text-xs text-gray-400 mb-4 flex-wrap">
                                                {featuredPost.categories?.[0] && (
                                                    <span className="bg-[#00d4ff]/15 text-[#00d4ff] border border-[#00d4ff]/30 px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider">
                                                        {featuredPost.categories[0].title}
                                                    </span>
                                                )}
                                                <div className="flex items-center gap-1.5">
                                                    <Calendar className="w-3.5 h-3.5 text-[#00d4ff]" />
                                                    <span>{formatDate(featuredPost.publishedAt)}</span>
                                                </div>
                                                {featuredPost.readTimeMinutes && (
                                                    <div className="flex items-center gap-1.5">
                                                        <Clock className="w-3.5 h-3.5 text-gray-500" />
                                                        <span>{featuredPost.readTimeMinutes} {t.blog_min_read || 'min'}</span>
                                                    </div>
                                                )}
                                            </div>

                                            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-title uppercase tracking-tight text-white group-hover:text-[#00d4ff] transition-colors leading-tight mb-4">
                                                {featuredPost.title}
                                            </h2>

                                            {featuredPost.excerpt && (
                                                <p className="text-gray-400 text-sm sm:text-base font-light leading-relaxed line-clamp-3 mb-6">
                                                    {featuredPost.excerpt}
                                                </p>
                                            )}
                                        </div>

                                        <div className="pt-6 border-t border-white/10 flex items-center justify-between">
                                            {featuredPost.author && (
                                                <div className="flex items-center gap-3">
                                                    {featuredPost.author.image?.asset ? (
                                                        <img 
                                                            src={urlFor(featuredPost.author.image).width(48).height(48).auto('format').url()} 
                                                            alt={featuredPost.author.name}
                                                            loading="lazy"
                                                            className="w-8 h-8 rounded-full object-cover border border-[#00d4ff]/40" 
                                                        />
                                                    ) : (
                                                        <div className="w-8 h-8 rounded-full bg-[#00d4ff]/20 text-[#00d4ff] flex items-center justify-center font-bold text-xs">
                                                            <User className="w-4 h-4" />
                                                        </div>
                                                    )}
                                                    <div className="flex flex-col text-left">
                                                        <span className="text-xs font-bold text-gray-200">{featuredPost.author.name}</span>
                                                        <span className="text-[10px] text-gray-500">{featuredPost.author.role || 'Guía Local'}</span>
                                                    </div>
                                                </div>
                                            )}
                                            
                                            <span className="inline-flex items-center gap-2 text-xs font-bold text-[#00d4ff] uppercase tracking-wider group-hover:translate-x-1 transition-transform">
                                                {t.blog_read_more || 'Leer artículo'} →
                                            </span>
                                        </div>
                                    </div>
                                </Link>
                            </div>
                        )}

                        {/* Grilla de Artículos Secundarios */}
                        {gridPosts.length > 0 && (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                                {gridPosts.map(post => (
                                    <article 
                                        key={post._id}
                                        className="group flex flex-col rounded-xl overflow-hidden border border-rock bg-[#14100d] transition-all duration-300 hover:border-[#00d4ff]/50 hover:-translate-y-1 shadow-xl"
                                    >
                                        <Link to={getLocalizedPath(`/blog/${post.slug.current}`, lang)} className="h-52 overflow-hidden relative block">
                                            <img 
                                                src={getPostImageUrl(post, false)} 
                                                alt={post.title}
                                                loading="lazy"
                                                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                                            />
                                            {post.categories?.[0] && (
                                                <span className="absolute top-3 left-3 bg-black/80 backdrop-blur-sm text-[#00d4ff] border border-[#00d4ff]/30 px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider">
                                                    {post.categories[0].title}
                                                </span>
                                            )}
                                        </Link>

                                        <div className="p-6 flex-1 flex flex-col justify-between">
                                            <div>
                                                <div className="flex items-center gap-3 text-[11px] text-gray-400 mb-3 flex-wrap">
                                                    <div className="flex items-center gap-1.5">
                                                        <Calendar className="w-3 h-3 text-[#00d4ff]" />
                                                        <span>{formatDate(post.publishedAt)}</span>
                                                    </div>
                                                    {post.readTimeMinutes && (
                                                        <div className="flex items-center gap-1.5">
                                                            <Clock className="w-3 h-3 text-gray-500" />
                                                            <span>{post.readTimeMinutes} {t.blog_min_read || 'min'}</span>
                                                        </div>
                                                    )}
                                                </div>

                                                <h3 className="text-xl font-title uppercase tracking-tight text-white group-hover:text-[#00d4ff] transition-colors leading-snug mb-3">
                                                    <Link to={`/blog/${post.slug.current}`}>{post.title}</Link>
                                                </h3>

                                                {post.excerpt && (
                                                    <p className="text-gray-400 text-xs sm:text-sm font-light leading-relaxed line-clamp-3 mb-6">
                                                        {post.excerpt}
                                                    </p>
                                                )}
                                            </div>

                                            <div className="pt-4 border-t border-white/5 flex items-center justify-between text-xs">
                                                <span className="text-[11px] font-bold text-gray-400">
                                                    {post.author ? `Por ${post.author.name}` : 'Fisura Expeditions'}
                                                </span>
                                                <Link 
                                                    to={`/blog/${post.slug.current}`} 
                                                    className="font-bold text-[#00d4ff] group-hover:translate-x-1 transition-transform inline-flex items-center gap-1"
                                                >
                                                    {t.blog_read_more || 'Leer'} →
                                                </Link>
                                            </div>
                                        </div>
                                    </article>
                                ))}
                            </div>
                        )}

                        {/* 5. Controles de Paginación */}
                        {totalPages > 1 && (
                            <div className="pt-12 border-t border-rock/60">
                                <nav aria-label="Paginación del Blog" className="flex flex-col sm:flex-row items-center justify-between gap-6">
                                    
                                    {/* Información de estado de página */}
                                    <div className="text-xs text-gray-400 order-2 sm:order-1 font-light">
                                        <span>{t.blog_page || 'Página'} </span>
                                        <span className="text-white font-bold">{currentPage}</span>
                                        <span> {t.blog_of || 'de'} </span>
                                        <span className="text-white font-bold">{totalPages}</span>
                                        <span className="text-gray-500 ml-2">({totalCount} {t.blog_articles_total || 'artículos'})</span>
                                    </div>

                                    {/* Botones de navegación */}
                                    <div className="flex items-center gap-1.5 sm:gap-2 order-1 sm:order-2">
                                        {/* Botón Anterior */}
                                        <button
                                            onClick={() => handlePageChange(currentPage - 1)}
                                            disabled={currentPage === 1}
                                            className={`flex items-center gap-1.5 px-3.5 py-2 rounded-xl border text-xs font-bold uppercase tracking-wider transition-all ${
                                                currentPage === 1
                                                    ? 'border-white/5 text-gray-600 cursor-not-allowed bg-white/[0.02]'
                                                    : 'border-white/10 text-gray-300 hover:border-[#00d4ff]/60 hover:text-[#00d4ff] hover:bg-white/5 active:scale-95 cursor-pointer'
                                            }`}
                                            aria-label="Página anterior"
                                        >
                                            <ChevronLeft className="w-4 h-4" />
                                            <span className="hidden sm:inline">{t.blog_prev || 'Anterior'}</span>
                                        </button>

                                        {/* Números de página */}
                                        <div className="flex items-center gap-1">
                                            {getPaginationNumbers(currentPage, totalPages).map((pageNum, idx) => {
                                                if (pageNum === '...') {
                                                    return (
                                                        <span key={`dots-${idx}`} className="w-8 h-8 flex items-center justify-center text-gray-500 text-xs select-none">
                                                            ...
                                                        </span>
                                                    );
                                                }
                                                const isCurrent = currentPage === pageNum;
                                                return (
                                                    <button
                                                        key={pageNum}
                                                        onClick={() => handlePageChange(pageNum as number)}
                                                        aria-current={isCurrent ? 'page' : undefined}
                                                        className={`w-9 h-9 sm:w-10 sm:h-10 rounded-xl text-xs font-bold flex items-center justify-center transition-all cursor-pointer ${
                                                            isCurrent
                                                                ? 'bg-[#00d4ff] text-black border border-[#00d4ff] shadow-lg shadow-[#00d4ff]/25 font-black scale-105'
                                                                : 'bg-white/5 border border-white/10 text-gray-300 hover:border-[#00d4ff]/50 hover:text-white hover:bg-white/10 active:scale-95'
                                                        }`}
                                                    >
                                                        {pageNum}
                                                    </button>
                                                );
                                            })}
                                        </div>

                                        {/* Botón Siguiente */}
                                        <button
                                            onClick={() => handlePageChange(currentPage + 1)}
                                            disabled={currentPage === totalPages}
                                            className={`flex items-center gap-1.5 px-3.5 py-2 rounded-xl border text-xs font-bold uppercase tracking-wider transition-all ${
                                                currentPage === totalPages
                                                    ? 'border-white/5 text-gray-600 cursor-not-allowed bg-white/[0.02]'
                                                    : 'border-white/10 text-gray-300 hover:border-[#00d4ff]/60 hover:text-[#00d4ff] hover:bg-white/5 active:scale-95 cursor-pointer'
                                            }`}
                                            aria-label="Página siguiente"
                                        >
                                            <span className="hidden sm:inline">{t.blog_next || 'Siguiente'}</span>
                                            <ChevronRight className="w-4 h-4" />
                                        </button>
                                    </div>
                                </nav>
                            </div>
                        )}

                    </div>
                )}

            </div>
        </div>
    );
};

export default Blog;

